import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Phone,
  Loader2,
  ArrowRight,
  Download,
  ExternalLink,
} from "lucide-react";
import {
  buildCsvUrl,
  formatDate,
  formatDuration,
  formatUsd,
  listConversations,
} from "@/lib/api";

function Stat({ label, value, tone = "black" }) {
  const toneCls = {
    black: "text-black",
    green: "text-[#16A34A]",
    red: "text-[#DC2626]",
    amber: "text-[#CA8A04]",
  }[tone];
  return (
    <div className="flex flex-col gap-1 border-l border-black/10 pl-3 py-2 min-w-0">
      <span className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-black/45">
        {label}
      </span>
      <span
        className={`font-mono-plex text-sm truncate ${toneCls}`}
        title={value == null ? "" : String(value)}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center border border-black/15 bg-black/[0.03] px-2 py-0.5 font-mono-plex text-[10px] uppercase tracking-[0.15em] text-black/75">
      {children}
    </span>
  );
}

function StatusPill({ status, callSuccessful }) {
  const primary = callSuccessful || status || "unknown";
  const map = {
    success: "text-[#16A34A] border-[#16A34A]/40 bg-[#16A34A]/5",
    failure: "text-[#DC2626] border-[#DC2626]/40 bg-[#DC2626]/5",
    done: "text-black/80 border-black/25",
    "in-progress": "text-[#CA8A04] border-[#CA8A04]/40 bg-[#CA8A04]/5",
    processing: "text-[#CA8A04] border-[#CA8A04]/40 bg-[#CA8A04]/5",
    failed: "text-[#DC2626] border-[#DC2626]/40 bg-[#DC2626]/5",
    unknown: "text-black/45 border-black/20",
  };
  const cls = map[primary] || map.unknown;
  return (
    <span
      className={`inline-flex items-center border px-2 py-0.5 font-mono-plex text-[9px] uppercase tracking-[0.15em] ${cls}`}
    >
      {String(primary).toUpperCase()}
    </span>
  );
}

function sentimentTone(sentiment) {
  if (!sentiment) return null;
  const s = String(sentiment).toLowerCase();
  if (s.includes("positive")) return { label: "Positive", color: "#16A34A" };
  if (s.includes("negative")) return { label: "Negative", color: "#DC2626" };
  if (s.includes("neutral")) return { label: "Neutral", color: "#6B7280" };
  return { label: sentiment, color: "#6B7280" };
}

export default function UserDetailDrawer({
  open,
  onOpenChange,
  user,
  onOpenConversation,
  onViewAllInConversations,
}) {
  const [sessions, setSessions] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageSize = 25;

  useEffect(() => {
    if (!open || !user?.phone) {
      setSessions([]);
      setTotalSessions(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listConversations({
      phone: user.phone,
      page: 1,
      page_size: pageSize,
      sort_by: "date_unix",
      sort_dir: "desc",
    })
      .then((data) => {
        if (cancelled) return;
        setSessions(data.items || []);
        setTotalSessions(data.total || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setSessions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, user?.phone]);

  if (!user) return null;

  const successRate = user.success_rate;
  const successTone =
    successRate == null
      ? "black"
      : successRate >= 75
      ? "green"
      : successRate >= 40
      ? "amber"
      : "red";

  const unixToLabel = (u) =>
    u && u > 0 ? formatDate(new Date(u * 1000).toISOString()) : "—";

  const csvHref = buildCsvUrl({ phone: user.phone });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="user-detail-drawer"
        className="w-full sm:max-w-2xl bg-white border-l border-black/15 text-black p-0 flex flex-col"
      >
        <SheetTitle className="sr-only">User details for {user.phone}</SheetTitle>
        <SheetDescription className="sr-only">
          Aggregate stats and recent session timeline for this caller.
        </SheetDescription>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-black/10 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-black/45" />
            <h2
              data-testid="user-drawer-phone"
              className="font-mono-plex text-lg text-black tracking-wide"
            >
              {user.phone}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-3 font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/50">
            <span>
              first seen{" "}
              <span className="text-black/80">
                {unixToLabel(user.first_contact_unix)}
              </span>
            </span>
            <span className="text-black/25">·</span>
            <span>
              last seen{" "}
              <span className="text-black/80">
                {unixToLabel(user.last_contact_unix)}
              </span>
            </span>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-1 gap-x-0 px-6 py-4 border-b border-black/10 bg-black/[0.015]">
          <Stat label="Sessions" value={user.sessions?.toLocaleString?.() ?? "—"} />
          <Stat label="Messages" value={user.total_messages?.toLocaleString?.() ?? "—"} />
          <Stat label="Duration" value={formatDuration(user.total_duration)} />
          <Stat label="Cost" value={formatUsd(user.total_cost_usd)} />
          <Stat label="Credits" value={user.total_credits_used?.toLocaleString?.() ?? "—"} />
          <Stat
            label="Success rate"
            value={successRate == null ? "—" : `${successRate.toFixed(1)}%`}
            tone={successTone}
          />
        </div>

        {/* Meta chips */}
        <div className="px-6 py-3 border-b border-black/10 flex flex-col gap-2">
          {(user.agents || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-black/45 w-16">
                Agents
              </span>
              {user.agents.map((a) => (
                <Chip key={a}>{a}</Chip>
              ))}
            </div>
          )}
          {(user.languages || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-black/45 w-16">
                Langs
              </span>
              {user.languages.map((l) => (
                <Chip key={l}>{l}</Chip>
              ))}
            </div>
          )}
          {(user.sources || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-black/45 w-16">
                Sources
              </span>
              {user.sources.map((s) => (
                <Chip key={s}>{s}</Chip>
              ))}
            </div>
          )}
        </div>

        {/* Session timeline */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 pt-4 pb-2 flex items-center justify-between sticky top-0 bg-white z-10 border-b border-black/5">
            <h3 className="font-mono-plex text-[10px] uppercase tracking-[0.25em] text-black/60">
              Session timeline
              <span className="ml-2 text-black/40">
                (showing {sessions.length} of {totalSessions.toLocaleString()})
              </span>
            </h3>
          </div>
          {loading ? (
            <div className="py-16 text-center">
              <Loader2 className="w-4 h-4 animate-spin inline-block text-black/40" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="py-16 text-center font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/40">
              no sessions found
            </div>
          ) : (
            <ol className="relative border-l border-black/10 ml-8 my-4 pr-6">
              {sessions.map((s) => {
                const senti = sentimentTone(s.sentiment_analysis);
                const title = s.call_summary_title || s.title || s.external_id;
                return (
                  <li
                    key={s.external_id}
                    data-testid={`user-session-${s.external_id}`}
                    className="relative mb-4 pl-5"
                  >
                    <span
                      className="absolute -left-[7px] top-2 w-3 h-3 rounded-full border border-black/25 bg-white"
                      style={{
                        background: senti ? senti.color : "white",
                        borderColor: senti ? senti.color : "rgba(0,0,0,0.25)",
                      }}
                    />
                    <button
                      onClick={() => onOpenConversation?.(s)}
                      className="w-full text-left block border border-black/10 hover:border-black/30 hover:bg-black/[0.02] p-3 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-black truncate" title={title}>
                            {title}
                          </div>
                          <div className="mt-1 font-mono-plex text-[10px] text-black/50 tracking-wide">
                            {formatDate(s.date)} · {formatDuration(s.duration)} ·{" "}
                            {s.messages_count || 0} msgs
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <StatusPill
                            status={s.status}
                            callSuccessful={s.call_successful}
                          />
                          <span className="font-mono-plex text-[11px] text-black/70">
                            {formatUsd(s.total_cost_usd)}
                          </span>
                        </div>
                      </div>
                      {senti && (
                        <div className="mt-2 flex items-center gap-1.5">
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full"
                            style={{ background: senti.color }}
                          />
                          <span
                            className="font-mono-plex text-[9px] uppercase tracking-[0.2em]"
                            style={{ color: senti.color }}
                          >
                            {senti.label}
                          </span>
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-black/10 px-6 py-3 flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-3 bg-white">
          <Button
            data-testid="user-view-all-conversations"
            variant="outline"
            onClick={() => onViewAllInConversations?.(user.phone)}
            className="flex-1 rounded-none border-black/20 text-black hover:bg-black/[0.04]"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-2" />
            View all conversations
            <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
          <a
            data-testid="user-export-sessions-csv"
            href={csvHref}
            className="flex-1 inline-flex items-center justify-center rounded-none border border-black bg-black text-white px-4 py-2 text-sm font-medium hover:bg-black/85 transition-colors"
          >
            <Download className="w-3.5 h-3.5 mr-2" />
            Export {totalSessions.toLocaleString()} sessions CSV
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
}
